/* LongevityScorePanel — DunedinPACE-style hybrid score
 * - Hybrid layer: 9 PhenoAge markers (auto-fill from latest lab)
 * - Extended layer: HbA1c, HDL, LDL, hs-CRP, ALT, eGFR, BP, BMI (optional)
 * Calls POST /api/longevity/calculate.
 */
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";
import { ComplianceDisclaimer, AIBadge, EvidenceBadge } from "@/components/compliance/ComplianceDisclaimer";
import SaveToHealthIntelligenceButton from "@/components/SaveToHealthIntelligenceButton";
import { Activity, Zap, ChevronRight } from "lucide-react";

const PHENO_FIELDS = [
  { id: "age", bg: "Хронологична възраст (години)", en: "Age (years)", min: 18, max: 110, step: 1 },
  { id: "albumin_g_dl", bg: "Албумин (g/dL)", en: "Albumin (g/dL)", min: 1.5, max: 6.5, step: 0.1 },
  { id: "creatinine_mg_dl", bg: "Креатинин (mg/dL)", en: "Creatinine (mg/dL)", min: 0.2, max: 15, step: 0.01 },
  { id: "glucose_mg_dl", bg: "Глюкоза (mg/dL)", en: "Glucose (mg/dL)", min: 40, max: 600, step: 1 },
  { id: "crp_mg_dl", bg: "CRP (mg/dL)", en: "CRP (mg/dL)", min: 0, max: 30, step: 0.01 },
  { id: "lymph_pct", bg: "Лимфоцити %", en: "Lymph %", min: 1, max: 80, step: 0.1 },
  { id: "mcv_fl", bg: "MCV (fL)", en: "MCV (fL)", min: 60, max: 130, step: 0.1 },
  { id: "rdw_pct", bg: "RDW %", en: "RDW %", min: 9, max: 30, step: 0.1 },
  { id: "alp_u_l", bg: "ALP (U/L)", en: "ALP (U/L)", min: 20, max: 600, step: 1 },
  { id: "wbc_k_ul", bg: "Левкоцити (10³/µL)", en: "WBC (10³/µL)", min: 1, max: 50, step: 0.1 },
];

const EXTENDED_FIELDS = [
  { id: "hba1c_pct", bg: "HbA1c %", en: "HbA1c %", step: 0.1, hint_bg: "Опт. <5.4", hint_en: "Optimal <5.4" },
  { id: "hdl_mmol_l", bg: "HDL (mmol/L)", en: "HDL (mmol/L)", step: 0.01, hint_bg: "Опт. ≥1.5", hint_en: "Optimal ≥1.5" },
  { id: "ldl_mmol_l", bg: "LDL (mmol/L)", en: "LDL (mmol/L)", step: 0.01, hint_bg: "Опт. <2.6", hint_en: "Optimal <2.6" },
  { id: "hs_crp_mg_l", bg: "hs-CRP (mg/L)", en: "hs-CRP (mg/L)", step: 0.01, hint_bg: "Опт. <1.0", hint_en: "Optimal <1.0" },
  { id: "alt_u_l", bg: "ALT (U/L)", en: "ALT (U/L)", step: 1, hint_bg: "Опт. 7-30", hint_en: "Optimal 7-30" },
  { id: "egfr_ml_min", bg: "eGFR (ml/min)", en: "eGFR (ml/min)", step: 1, hint_bg: "Опт. ≥90", hint_en: "Optimal ≥90" },
  { id: "systolic_bp", bg: "Систолно (mmHg)", en: "Systolic (mmHg)", step: 1, hint_bg: "Опт. 110-120", hint_en: "Optimal 110-120" },
  { id: "diastolic_bp", bg: "Диастолно (mmHg)", en: "Diastolic (mmHg)", step: 1, hint_bg: "Опт. 70-80", hint_en: "Optimal 70-80" },
  { id: "bmi", bg: "BMI (kg/m²)", en: "BMI (kg/m²)", step: 0.1, hint_bg: "Опт. 20-25", hint_en: "Optimal 20-25" },
];

function ScoreGauge({ score, isDark }) {
  // 0-100 vertical-ish bar with color zones
  let color = "bg-rose-500";
  if (score >= 80) color = "bg-emerald-500";
  else if (score >= 60) color = "bg-sky-500";
  else if (score >= 40) color = "bg-amber-500";
  return (
    <div className={`h-3 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
      <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
    </div>
  );
}

export default function LongevityScorePanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [phen, setPhen] = useState({});
  const [ext, setExt] = useState({});
  const [gender, setGender] = useState("M");
  const [showExt, setShowExt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [result, setResult] = useState(null);

  const setVal = (setter) => (id, v) => {
    setter((prev) => ({ ...prev, [id]: v === "" ? "" : Number(v) }));
  };

  const handleAutofill = async () => {
    setAutofilling(true);
    try {
      const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
      const { data } = await axios.get(`${API}/biological-age/from-latest-lab`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const next = { ...phen };
      if (data.age) next.age = data.age;
      Object.entries(data.markers || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined) next[k] = Number(v);
      });
      setPhen(next);
      const missing = (data.missing_fields || []).length;
      if (missing === 0 && data.age) toast.success(bg ? "Готово за изчисление" : "Ready to calculate");
      else toast.info(bg ? `Попълнени основни маркери (${missing} липсват)` : `Filled core markers (${missing} missing)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Не успяхме да попълним от лаборатория" : "Could not autofill"));
    } finally {
      setAutofilling(false);
    }
  };

  const handleCalculate = async () => {
    // Validate core fields present
    for (const f of PHENO_FIELDS) {
      if (phen[f.id] === undefined || phen[f.id] === "" || phen[f.id] === null) {
        toast.error(bg ? `Попълнете: ${f.bg}` : `Fill: ${f.en}`);
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { ...phen, gender, save: true };
      // Add extended fields only if they have a value
      Object.entries(ext).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && !Number.isNaN(v)) payload[k] = v;
      });
      const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
      const { data } = await axios.post(`${API}/longevity/calculate`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResult(data);
      toast.success(bg ? "Longevity Score изчислен" : "Longevity Score calculated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при изчислението" : "Calculation error"));
    } finally {
      setLoading(false);
    }
  };

  const cardCls = isDark
    ? "bg-[hsl(223,47%,12%)] border-white/5"
    : "bg-white border-gray-200";

  return (
    <CollapsiblePanel
      id="longevity-score-panel"
      title={bg ? "Longevity Score (DunedinPACE-style)" : "Longevity Score (DunedinPACE-style)"}
      icon={Activity}
      iconColor="#06B6D4"
      data-testid="longevity-score-panel"
    >
      <div className="space-y-4">
        {/* Header info */}
        <div className={`rounded-xl border p-3 ${isDark ? "bg-cyan-500/5 border-cyan-900/30" : "bg-cyan-50 border-cyan-200"}`}>
          <div className="flex items-start gap-2">
            <Activity className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs space-y-1">
              <div className="font-semibold">
                {bg
                  ? "Хибриден темп на стареене базиран на рутинни лаборатории"
                  : "Hybrid pace-of-aging from routine bloodwork"}
              </div>
              <div className={isDark ? "text-gray-400" : "text-gray-600"}>
                {bg
                  ? "Levine PhenoAge (9 маркера) + DunedinPACE-стилово ratio. Незадължителен Extended layer добавя HbA1c/lipids/BP/BMI за по-богат сигнал."
                  : "Levine PhenoAge (9 markers) + DunedinPACE-style ratio. Optional Extended layer adds HbA1c/lipids/BP/BMI for richer signal."}
              </div>
            </div>
          </div>
        </div>

        {/* Autofill button */}
        <button
          onClick={handleAutofill}
          disabled={autofilling}
          data-testid="longevity-autofill-btn"
          className={`w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold border transition-colors
            ${isDark ? "border-violet-900/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
                     : "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"}
            disabled:opacity-50`}
        >
          <Zap className="w-3.5 h-3.5" />
          {autofilling ? (bg ? "Попълвам..." : "Filling...") : (bg ? "Попълни от последна лаборатория" : "Autofill from latest lab")}
        </button>

        {/* Core PhenoAge markers grid */}
        <div className="grid grid-cols-2 gap-2">
          {PHENO_FIELDS.map((f) => (
            <label key={f.id} className="block">
              <span className={`text-[11px] block mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? f.bg : f.en}
              </span>
              <input
                type="number"
                step={f.step}
                min={f.min}
                max={f.max}
                value={phen[f.id] ?? ""}
                onChange={(e) => setVal(setPhen)(f.id, e.target.value)}
                data-testid={`longevity-input-${f.id}`}
                className={`w-full px-2 py-1.5 text-xs rounded-md border outline-none
                  ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white"
                           : "bg-white border-gray-200 text-gray-900"}`}
              />
            </label>
          ))}
        </div>

        {/* Gender (used for HDL scoring) */}
        <label className="flex items-center gap-2 text-xs">
          <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Пол" : "Gender"}:</span>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className={`px-2 py-1 rounded-md border text-xs
              ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white"
                       : "bg-white border-gray-200 text-gray-900"}`}
            data-testid="longevity-gender"
          >
            <option value="M">{bg ? "Мъж" : "Male"}</option>
            <option value="F">{bg ? "Жена" : "Female"}</option>
          </select>
        </label>

        {/* Toggle for extended */}
        <button
          onClick={() => setShowExt((v) => !v)}
          className={`text-xs inline-flex items-center gap-1 ${isDark ? "text-cyan-400" : "text-cyan-700"}`}
          data-testid="longevity-toggle-extended"
        >
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showExt ? "rotate-90" : ""}`} />
          {bg ? "Extended biomarkers (по желание)" : "Extended biomarkers (optional)"}
        </button>

        {showExt && (
          <div className="grid grid-cols-2 gap-2" data-testid="longevity-extended-grid">
            {EXTENDED_FIELDS.map((f) => (
              <label key={f.id} className="block">
                <span className={`text-[11px] block mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {bg ? f.bg : f.en}
                  <span className={`ml-1 text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {bg ? f.hint_bg : f.hint_en}
                  </span>
                </span>
                <input
                  type="number"
                  step={f.step}
                  value={ext[f.id] ?? ""}
                  onChange={(e) => setVal(setExt)(f.id, e.target.value)}
                  data-testid={`longevity-ext-${f.id}`}
                  className={`w-full px-2 py-1.5 text-xs rounded-md border outline-none
                    ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white"
                             : "bg-white border-gray-200 text-gray-900"}`}
                />
              </label>
            ))}
          </div>
        )}

        {/* Calculate */}
        <button
          onClick={handleCalculate}
          disabled={loading}
          data-testid="longevity-calculate-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold
            bg-gradient-to-r from-cyan-600 to-sky-600 text-white hover:opacity-95 disabled:opacity-50"
        >
          {loading ? (bg ? "Изчислявам..." : "Calculating...") : (bg ? "Изчисли Longevity Score" : "Calculate Longevity Score")}
        </button>

        {/* Result */}
        {result && (
          <div className={`rounded-xl border p-3 space-y-3 ${cardCls}`} data-testid="longevity-result">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="text-sm font-bold flex items-center gap-1.5">
                  {bg ? "Резултат" : "Result"}
                  <AIBadge />
                  <EvidenceBadge label={bg ? "Levine 2018" : "Levine 2018"} />
                </h4>
                <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? `Слой: ${result.tier === "extended" ? "Hybrid + Extended" : "Hybrid (PhenoAge)"}` : `Layer: ${result.tier === "extended" ? "Hybrid + Extended" : "Hybrid (PhenoAge)"}`}
                </p>
              </div>
            </div>

            {/* Headline score */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-lg p-3 text-center border ${isDark ? "border-white/5" : "border-gray-200"}`}>
                <div className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {bg ? "Longevity Score" : "Longevity Score"}
                </div>
                <div className="text-2xl font-extrabold mt-1">{result.longevity_score}</div>
                <div className="mt-2"><ScoreGauge score={result.longevity_score} isDark={isDark} /></div>
              </div>
              <div className={`rounded-lg p-3 text-center border ${isDark ? "border-white/5" : "border-gray-200"}`}>
                <div className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {bg ? "Pace of Aging" : "Pace of Aging"}
                </div>
                <div className="text-2xl font-extrabold mt-1">{result.pace_of_aging?.toFixed(2)}</div>
                <div className={`text-[11px] mt-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {bg ? result.pace_bucket?.label_bg : result.pace_bucket?.label_en}
                </div>
              </div>
            </div>

            {/* Bio age vs chronological */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`rounded-lg p-2 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                <div className={isDark ? "text-gray-500" : "text-gray-500"}>{bg ? "Хронологична" : "Chronological"}</div>
                <div className="font-bold text-base">{result.phenoage?.chronological_age}</div>
              </div>
              <div className={`rounded-lg p-2 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                <div className={isDark ? "text-gray-500" : "text-gray-500"}>{bg ? "Биологична (PhenoAge)" : "Biological (PhenoAge)"}</div>
                <div className="font-bold text-base">{result.phenoage?.biological_age}</div>
              </div>
            </div>

            {/* Extended breakdown */}
            {result.extended && typeof result.extended === "object" && Object.keys(result.extended).length > 0 && (
              <div>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {bg ? "Разбивка по маркер" : "Per-marker breakdown"}
                </div>
                <div className="space-y-1.5" data-testid="longevity-breakdown">
                  {Object.entries(result.extended).map(([k, v]) => {
                    // Defensive: ако backend върне число вместо {value, score}, не chash-вай
                    const safeValue = (v && typeof v === "object") ? v.value : v;
                    const safeScore = (v && typeof v === "object" && typeof v.score === "number") ? v.score : 0;
                    return (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <span className={`w-24 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{k.toUpperCase()}</span>
                        <span className={`w-12 ${isDark ? "text-gray-300" : "text-gray-800"}`}>{safeValue != null ? String(safeValue) : "—"}</span>
                        <div className="flex-1"><ScoreGauge score={safeScore} isDark={isDark} /></div>
                        <span className="w-8 text-right font-semibold">{safeScore || "—"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className={`text-[11px] italic ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {(bg ? result.interpretation_bg : result.interpretation_en) || ""}
            </p>

            {/* iter425 — Save to Health Intelligence */}
            <div className="flex justify-end pt-2">
              <SaveToHealthIntelligenceButton
                snapshotType="lab_panel"
                title={bg
                  ? `Longevity Score · ${result.longevity_score}/100`
                  : `Longevity Score · ${result.longevity_score}/100`}
                summary={bg
                  ? `PhenoAge: ${result.phenoage?.biological_age || "—"}г · Pace of Aging: ${result.pace_of_aging?.toFixed(2) || "—"}`
                  : `PhenoAge: ${result.phenoage?.biological_age || "—"}y · Pace of Aging: ${result.pace_of_aging?.toFixed(2) || "—"}`}
                payload={{
                  longevity_score: result.longevity_score,
                  pace_of_aging: result.pace_of_aging,
                  pace_bucket: result.pace_bucket,
                  phenoage: result.phenoage,
                  tier: result.tier,
                  extended: result.extended,
                  computed_at: new Date().toISOString(),
                }}
                tags={["longevity", "phenoage", "Levine-2018"]}
                size="sm"
                testId="longevity-save-to-hi-btn"
              />
            </div>

            <ComplianceDisclaimer context="clinical" variant="moderate" />
          </div>
        )}
      </div>
    </CollapsiblePanel>
  );
}
